import {
	Button,
	Col,
	Form,
	Input,
	message,
	Modal,
	Row,
	Space,
	Spin,
	Tabs,
	TabsProps,
	Tag,
	Typography,
	Table,
	Tooltip,
} from "antd";
import React, { useEffect, useState } from "react";
import { getErrorMessage } from "../../../helpers/errorHandler";
import { httpRequest } from "../../../helpers/api";
import useAuth from "../../../hooks/useAuth";
import { PortalProperties } from "../../../types/portal.type";
import { BaseResponsePaginationProps, BaseResponseProps } from "../../../types/config.type";
import DetailItem from "../../../components/DetailItem";
import { OfficePortalProfileProps } from "../../../types/portalProfile.type";
import { CloseOutlined, EyeOutlined, PlusOutlined } from "@ant-design/icons";
import NotSet from "../../../components/NotSet";
import { generateFormRules, validateEmailPortal } from "../../../helpers/formRules";
import InputPrivateEmail from "../../../components/form/InputPrivateEmail";
import { CLIENT_CMS, CLIENT_MOBILE, LAMUDI_ID, R123_PORTAL_ID } from "../../employee/constant";
import { confirmModal } from "../../../helpers/modal";
import PortalProfileOTPModal from "../../../components/PortalProfileOTPModal";
import { ColumnType } from "antd/lib/table";
import { renderRoles } from "../../../helpers/renderRoles";
import useFetchList from "../../../hooks/useFetchList";
import CustomPagination from "../../../components/CustomPagination";
import CompareDataView from "../../../components/CompareDataView";
import { formatYearToTime } from "../../../helpers/format";

export type UserActivityLogProperties = {
	userActivityLogId: string;
	userId: string;
	officeId?: string;
	employeeId?: string;
	metadata?: any;
	actionType: string;
	repositoryName: string;
	affectedId: string;
	previousData?: any;
	newData?: any;
	clientId?: string;
	clientVersion?: string;
	updatedAt?: Date;
	createdAt?: Date;
};
interface IProps {
	officeId?: string;
	doneSyncPortal?: boolean;
	onSuccess?: () => void;
}
export default function OfficePortalConnection({ officeId, doneSyncPortal, onSuccess }: IProps) {
	const [isLoading, setIsLoading] = useState(false);
	const [isLoadingPortal, setIsLoadingPortal] = useState(false);
	const [showModalOTP, setShowModalOTP] = useState(false);
	const [showLogs, setShowLogs] = useState(false);
	const [tab, setTab] = useState("");
	const [showDiffLog, setShowDiffLog] = useState<UserActivityLogProperties | undefined>(undefined);

	const { isHQ } = useAuth();
	const [portals, setPortals] = useState<PortalProperties[]>([]);
	const [officePortalProfiles, setOfficePortalProfiles] = useState<OfficePortalProfileProps[]>([]);
	const [tempData, setTempData] = useState<PortalProperties | undefined>();
	const [form] = Form.useForm();
	const watchEmail = Form.useWatch("email", form);

	const {
		isLoading: isLoadingLogs,
		data: logs,
		pagination,
		changePage,
		query,
		setQuery,
		fetchList,
	} = useFetchList<UserActivityLogProperties, any>({
		endpoint: "user-activity-logs",
		initialQuery: {
			filterAffectedPortalId: tab,
			filterAffectedOfficeId: officeId,
			filterRepositoryName: "office_portal_profiles",
		},
		fetchable: showLogs && portals.length > 0,
		limit: 10,
	});

	useEffect(() => {
		if (showLogs) {
			setTab(portals[0].portalId);
			setQuery({
				...query,
				filterAffectedPortalId: portals[0].portalId,
			});
		}
	}, [portals, showLogs]);

	useEffect(() => {
		(async () => {
			setIsLoadingPortal(true);
			try {
				const res = await httpRequest.get<BaseResponsePaginationProps<PortalProperties>>("/portals", {
					params: {
						filterIsActive: "true",
					},
				});

				setPortals(res.data.payload.results);
				setQuery({
					...query,
					filterAffectedPortalId: res.data.payload.results[0].portalId,
				});
				setTab(res.data.payload.results[0].portalId);
			} catch (error) {
				message.error(getErrorMessage(error));
			} finally {
				setIsLoadingPortal(false);
			}
		})();
	}, []);

	const getOfficePortalProfiles = async () => {
		if (!officeId && portals.length === 0) {
			return;
		}
		setIsLoadingPortal(true);
		try {
			const resProfiles = await httpRequest.get<BaseResponsePaginationProps<OfficePortalProfileProps>>(
				`${isHQ ? "" : "/office"}/office-portal-profiles`,
				{
					params: {
						filterOfficeId: officeId,
						filterPortalId: portals.map((v) => v.portalId).join(","),
					},
				}
			);
			setOfficePortalProfiles(resProfiles.data.payload.results);
		} catch (error) {
			message.error(getErrorMessage(error));
		} finally {
			setIsLoadingPortal(false);
		}
	};

	useEffect(() => {
		getOfficePortalProfiles();
	}, [officeId, portals, doneSyncPortal]);

	const handleSubmit = async () => {
		if (!isHQ) {
			setShowModalOTP(true);

			return;
		}
		setIsLoading(true);
		try {
			const values = form.getFieldsValue();

			await httpRequest.post(`${isHQ ? "" : "/office"}/office-portal-profiles`, {
				portalId: tempData?.portalId,
				officeId,
				portalUsername: values.email,
				originUrl: window.location.href,
			});
			message.success(`Successfully added ${tempData?.portalName} email`);
			setTempData(undefined);
			form.resetFields();
			getOfficePortalProfiles();
			onSuccess?.();
		} catch (error) {
			message.error(getErrorMessage(error));
		} finally {
			setIsLoading(false);
		}
	};

	const handleBeforeSubmit = async () => {
		setIsLoading(true);
		try {
			const values = form.getFieldsValue();

			if (isHQ) {
				const { data } = await httpRequest.post<BaseResponseProps<{ isPassed: boolean; message: string; requireOTP?: boolean }>>(
					"office-portal-profiles/validate-email",
					{
						email: values.email,
						portalId: tempData?.portalId,
						officeId,
					}
				);

				if (data.payload.isPassed) {
					// Check if OTP is required (for Lamudi new publisher)
					if (data.payload.requireOTP) {
						setShowModalOTP(true);
					} else {
						handleSubmit();
					}
				} else {
					confirmModal({
						title: "Confirmation",
						subtitle: data.payload.message,
						onOk: () => handleSubmit(),
					});
				}
			} else {
				handleSubmit();
			}
		} catch (error) {
			message.error(getErrorMessage(error));
		} finally {
			setIsLoading(false);
		}
	};

	const handleDelete = async (data: OfficePortalProfileProps) => {
		setIsLoading(true);
		try {
			await httpRequest.delete(`${isHQ ? "" : "/office"}/office-portal-profiles/${data.officePortalProfileId}`, {
				params: {
					originUrl: window.location.href,
				},
			});
			getOfficePortalProfiles();
			onSuccess?.();
			message.success("Successfully deleted");
		} catch (error) {
			message.error(getErrorMessage(error));
		} finally {
			setIsLoading(false);
		}
	};

	const COLUMNS: ColumnType<UserActivityLogProperties>[] = [
		{
			title: "ACTION TYPE",
			key: "actionType",
			dataIndex: "actionType",
			render(value: string) {
				return value ? value.split("_").join(" ") : <NotSet />;
			},
		},
		{
			title: "ACTION BY",
			dataIndex: ["metadata", "userName"],
			key: "userName",
			render: (name: string, record: UserActivityLogProperties) => (
				<>
					{name ?? "SYSTEM"} (Roles:{" "}
					{record?.metadata?.actionRoleUser ? (
						renderRoles(record.metadata?.actionRoleUser.split(","))
					) : (
						<NotSet />
					)}
					)
				</>
			),
		},
		{
			title: "PLATFORM",
			dataIndex: "clientId",
			key: "clientId",
			render: (clientId: string) =>
				clientId === CLIENT_CMS ? (
					<Tag color="blue">CMS</Tag>
				) : clientId === CLIENT_MOBILE ? (
					<Tag color="green">MOBILE</Tag>
				) : (
					<NotSet />
				),
		},
		{
			title: "VERSION",
			dataIndex: "clientVersion",
			key: "clientVersion",
			render: (clientVersion: string, record: UserActivityLogProperties) =>
				record.clientId === CLIENT_CMS ? (
					<Tag color="blue">{clientVersion}</Tag>
				) : record.clientId === CLIENT_MOBILE ? (
					<Tag color="green">{clientVersion}</Tag>
				) : (
					<NotSet />
				),
		},
		{
			title: "ACTION FROM",
			dataIndex: "originUrl",
			key: "originUrl",
			width: 150,
			render: (_: string, record: UserActivityLogProperties) =>
				record?.metadata?.originUrl ? record?.metadata?.originUrl : <NotSet />,
		},
		{
			title: "DIFF. DATA",
			dataIndex: "diffData",
			key: "diffData",
			render: (_: string, record: UserActivityLogProperties) => {
				return <Button onClick={() => setShowDiffLog(record)}>Show Different Data</Button>;
			},
		},
		{
			title: "CREATED AT",
			dataIndex: "createdAt",
			key: "createdAt",
			render: (createdAt: Date) => {
				return formatYearToTime(createdAt);
			},
		},
	];

	return (
		<Spin spinning={isLoadingPortal}>
			<Space
				style={{
					marginTop: 20,
					marginBottom: 10,
				}}
				size={12}
			>
				<Typography.Title
					style={{ margin: 0 }}
					level={4}
				>
					Portal Connection
				</Typography.Title>
				<Tooltip title="Show Portal Email Logs">
					<a
						onClick={() => {
							setShowLogs(true);
							fetchList();
						}}
					>
						<EyeOutlined />
					</a>
				</Tooltip>
			</Space>
			<Row align="top">
				{portals.map((portal) => {
					const officeProfilesByPortal = officePortalProfiles.filter((v) => v.portalId === portal.portalId);
					return (
						<Col span={12}>
							<DetailItem
								strongLabel={true}
								key={portal.portalId}
								label={`${portal.portalName} ${
									officeProfilesByPortal.length > 0 && portal.portalId === R123_PORTAL_ID
										? `| Office Portal ID: ${officeProfilesByPortal
												.map((v) => v.companyId && v.companyId)
												.join(", ")}`
										: ""
								}`}
							>
								<Space
									wrap
									style={{ marginTop: 8 }}
								>
									{officeProfilesByPortal.length > 0 ? (
										officeProfilesByPortal.map((item) => (
											<Tag
												key={item.officePortalProfileId}
												color={item.portalId === R123_PORTAL_ID ? "#2951A3" : "#FF8600"}
											>
												<Space>
													<Space
														direction="vertical"
														size={0}
													>
														{item.portalUsername}
													</Space>
													<div
														style={{ cursor: "pointer" }}
														onClick={() => {
															confirmModal({
																title: "Deleted Portal Email",
																subtitle: `Are you sure want to delete this? Listings associated with this email will be removed from the ${item.portal?.portalName} portal.`,
																onOk: () => handleDelete(item),
																danger: true,
															});
														}}
													>
														<CloseOutlined style={{ fontSize: 16 }} />
													</div>
												</Space>
											</Tag>
										))
									) : (
										<NotSet />
									)}
									<Button
										type="primary"
										onClick={() => {
											if (officeProfilesByPortal.length >= 5) {
												return message.error("A maximum of only 5 accounts can be added.");
											}

											setTempData(portal);
										}}
									>
										<PlusOutlined />
									</Button>
								</Space>
							</DetailItem>
						</Col>
					);
				})}
			</Row>

			<Modal
				title={`Add Email ${tempData?.portalName}`}
				open={!!tempData}
				onCancel={() => {
					setTempData(undefined);
					form.resetFields();
				}}
				destroyOnClose={true}
				width={520}
				footer={null}
			>
				<Form
					form={form}
					layout="vertical"
					autoComplete="off"
					onFinish={handleBeforeSubmit}
				>
					<Form.Item
						label="Email"
						name="email"
						rules={[
							...generateFormRules("Email", ["required", "email"]),
							!isHQ
								? {
										validator(rule, value, callback) {
											return validateEmailPortal(
												{ value, portalId: tempData?.portalId!, officeId },
												"office"
											);
										},
								  }
								: {},
						]}
					>
						<InputPrivateEmail
							placeholder="Enter Email"
							allowClear={true}
						/>
					</Form.Item>
					{tempData?.portalId === LAMUDI_ID && (
						<Typography.Text
							type="warning"
							style={{ fontSize: 12, display: "block", marginBottom: 12 }}
						>
							* If your email is not registered with Lamudi, you will need to verify via OTP to create a new publisher account.
						</Typography.Text>
					)}
					<Button
						htmlType="submit"
						block
						type="primary"
						disabled={!watchEmail}
						loading={isLoading}
					>
						Add
					</Button>
				</Form>
			</Modal>

			<PortalProfileOTPModal
				data={{
					email: watchEmail,
					portalId: tempData?.portalId!,
					officeId: officeId!,
				}}
				type="office"
				open={showModalOTP}
				onClose={() => setShowModalOTP(false)}
				onSuccess={() => {
					message.success(`Successfully added ${tempData?.portalName} email`);
					setTempData(undefined);
					form.resetFields();
					getOfficePortalProfiles();
					onSuccess?.();
				}}
			/>

			<Modal
				title={"Portal Connection Logs"}
				open={showLogs}
				onCancel={() => {
					setShowLogs(false);
					setTab("");
				}}
				destroyOnClose={true}
				width="80%"
				footer={null}
			>
				<Tabs
					renderTabBar={(props, TabNavList) => (
						<TabNavList
							{...props}
							mobile={true}
						/>
					)}
					activeKey={tab}
					onChange={(newTab) => {
						setTab(newTab);
						setQuery({
							...query,
							filterAffectedPortalId: newTab,
						});
					}}
					onTabClick={(key) => {
						setTab(key);
						setQuery({
							...query,
							filterAffectedPortalId: key,
						});
					}}
					items={
						portals
							.map((portal) => ({
								key: portal.portalId,
								label: `${portal.portalName} Email Logs`,
								children: <></>,
							}))
							.filter(Boolean) as TabsProps["items"]
					}
				/>

				<Table
					className="no-lr-padding"
					loading={isLoadingLogs}
					columns={COLUMNS}
					dataSource={logs}
					pagination={false}
					rowKey={"userActivityLogId"}
					scroll={{ x: "100%" }}
				/>

				<CustomPagination
					current={pagination.page}
					total={pagination.totalData}
					defaultPageSize={pagination.perPage}
					pageSize={pagination.perPage}
					onChange={changePage}
				/>
			</Modal>

			<Modal
				title={"Different Data Log"}
				open={!!showDiffLog}
				onCancel={() => {
					setShowDiffLog(undefined);
				}}
				destroyOnClose={true}
				width="80%"
				footer={null}
			>
				<CompareDataView
					oldData={showDiffLog?.previousData}
					newData={showDiffLog?.newData}
					mode="json"
				/>
			</Modal>
		</Spin>
	);
}
